import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Switch, Spin, Alert, Upload, Space, Typography } from 'antd';
import { SaveOutlined, WechatOutlined, AlipayOutlined, BankOutlined, UploadOutlined, FileOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Text } = Typography;

const PaymentSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [form] = Form.useForm();
  const [wechatpayEnabled, setWechatpayEnabled] = useState(false);
  const [wechatpaySandbox, setWechatpaySandbox] = useState(true);
  const [alipayEnabled, setAlipayEnabled] = useState(false);
  const [alipaySandbox, setAlipaySandbox] = useState(true);
  const [offlineEnabled, setOfflineEnabled] = useState(true);
  const [wechatCertPath, setWechatCertPath] = useState('');
  const [alipayCertPath, setAlipayCertPath] = useState('');
  const [wechatpayPrivateKey, setWechatpayPrivateKey] = useState('');
  const [wechatpayCertSerial, setWechatpayCertSerial] = useState('');
  const [wechatpayApiV3Key, setWechatpayApiV3Key] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getConfigs();
      
      let configsArray = [];
      if (response.code === 0) {
        if (Array.isArray(response.data)) {
          configsArray = response.data;
        } else if (response.data && response.data.list && Array.isArray(response.data.list)) {
          configsArray = response.data.list;
        } else if (response.data && typeof response.data === 'object') {
          configsArray = Object.entries(response.data).map(([key, value]) => ({
            config_key: key,
            config_value: value
          }));
        }
      }
      
      if (configsArray.length > 0) {
        const cfgMap = {};
        configsArray.forEach((item) => {
          cfgMap[item.config_key] = item.config_value;
        });
        
        form.setFieldsValue({
          // 微信支付配置
          wechatpay_app_id: cfgMap.wechatpay_app_id || '',
          wechatpay_mch_id: cfgMap.wechatpay_mch_id || '',
          wechatpay_api_key: cfgMap.wechatpay_api_key || '',
          wechatpay_cert_path: cfgMap.wechatpay_cert_path || '',
          wechatpay_notify_url: cfgMap.wechatpay_notify_url || '',
          // 支付宝配置
          alipay_app_id: cfgMap.alipay_app_id || '',
          alipay_private_key: cfgMap.alipay_private_key || '',
          alipay_public_key: cfgMap.alipay_public_key || '',
          alipay_notify_url: cfgMap.alipay_notify_url || '',
          alipay_return_url: cfgMap.alipay_return_url || '',
          // 银行卡支付配置
          bank_account: cfgMap.bank_account || '',
          bank_name: cfgMap.bank_name || '',
          bank_account_name: cfgMap.bank_account_name || '',
        });
        
        setWechatpayEnabled(cfgMap.wechatpay_enabled === 'true');
        setWechatpaySandbox(cfgMap.wechatpay_sandbox === 'true');
        setAlipayEnabled(cfgMap.alipay_enabled === 'true');
        setAlipaySandbox(cfgMap.alipay_sandbox === 'true');
        setOfflineEnabled(cfgMap.offline_enabled !== 'false');
        setWechatCertPath(cfgMap.wechatpay_cert_path || '');
        setAlipayCertPath(cfgMap.alipay_cert_path || '');
        setWechatpayPrivateKey(cfgMap.wechatpay_private_key || '');
        setWechatpayCertSerial(cfgMap.wechatpay_cert_serial || '');
        setWechatpayApiV3Key(cfgMap.wechatpay_api_v3_key || '');
      }
    } catch (error) {
      console.error('获取配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      
      const configsList = [
        // 微信支付配置
        { key: 'wechatpay_app_id', value: values.wechatpay_app_id || '' },
        { key: 'wechatpay_mch_id', value: values.wechatpay_mch_id || '' },
        { key: 'wechatpay_api_key', value: values.wechatpay_api_key || '' },
        { key: 'wechatpay_cert_path', value: wechatCertPath || '' },
        { key: 'wechatpay_notify_url', value: values.wechatpay_notify_url || '' },
        { key: 'wechatpay_enabled', value: wechatpayEnabled ? 'true' : 'false' },
        { key: 'wechatpay_sandbox', value: wechatpaySandbox ? 'true' : 'false' },
        { key: 'wechatpay_private_key', value: wechatpayPrivateKey || '' },
        { key: 'wechatpay_cert_serial', value: wechatpayCertSerial || '' },
        { key: 'wechatpay_api_v3_key', value: wechatpayApiV3Key || '' },
        // 支付宝配置
        { key: 'alipay_app_id', value: values.alipay_app_id || '' },
        { key: 'alipay_private_key', value: values.alipay_private_key || '' },
        { key: 'alipay_public_key', value: values.alipay_public_key || '' },
        { key: 'alipay_notify_url', value: values.alipay_notify_url || '' },
        { key: 'alipay_return_url', value: values.alipay_return_url || '' },
        { key: 'alipay_enabled', value: alipayEnabled ? 'true' : 'false' },
        { key: 'alipay_sandbox', value: alipaySandbox ? 'true' : 'false' },
        { key: 'alipay_cert_path', value: alipayCertPath || '' },
        // 银行卡支付配置
        { key: 'offline_enabled', value: offlineEnabled ? 'true' : 'false' },
        { key: 'bank_account', value: values.bank_account || '' },
        { key: 'bank_name', value: values.bank_name || '' },
        { key: 'bank_account_name', value: values.bank_account_name || '' },
      ];

      for (const cfg of configsList) {
        await adminAPI.updateConfigByKey(cfg.key, cfg.value);
      }

      message.success('支付配置保存成功');
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    } finally {
      setSaving(false);
    }
  };

  // 上传证书
  const handleUploadCert = async (certType, file) => {
    setUploadingCert(true);
    try {
      const formData = new FormData();
      formData.append('cert_type', certType);
      formData.append('file', file);
      
      const response = await adminAPI.uploadPaymentCert(certType, file);
      if (response.code === 0) {
        if (certType === 'wechatpay_cert') {
          setWechatCertPath(response.data.file_path);
          message.success('微信支付证书上传成功');
        } else if (certType === 'alipay_cert') {
          setAlipayCertPath(response.data.file_path);
          message.success('支付宝证书上传成功');
        }
      } else {
        message.error(response.message || '证书上传失败');
      }
    } catch (error) {
      console.error('证书上传失败:', error);
      message.error('证书上传失败');
    } finally {
      setUploadingCert(false);
    }
    return false; // 阻止默认上传行为
  };

  return (
    <div className="admin-page-wrapper">
      <div className="admin-page-container">
        <div className="page-header">
          <h2>支付账户配置</h2>
          <p>配置微信支付、支付宝和银行卡转账账户信息</p>
        </div>

        <Spin spinning={loading}>
          <Form form={form} layout="vertical">
            {/* 微信支付配置 */}
            <Card
              title={
                <span>
                  <WechatOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                  微信支付
                </span>
              }
              style={{ marginBottom: 24 }}
              extra={
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={wechatpayEnabled}
                  onChange={setWechatpayEnabled}
                />
              }
            >
              <Alert
                message="获取微信支付 API 凭证"
                description={
                  <span>
                    请前往 <a href="https://pay.weixin.qq.com/" target="_blank" rel="noopener noreferrer">微信支付商户平台</a> 获取配置信息。
                    Webhook 端点地址为：<code>/api/webhook/wechatpay</code>
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="wechatpay_app_id"
                label="微信支付 App ID"
                rules={[{ required: wechatpayEnabled, message: '请输入微信支付 App ID' }]}
              >
                <Input placeholder="微信公众平台的应用ID" />
              </Form.Item>
              <Form.Item
                name="wechatpay_mch_id"
                label="微信支付商户号"
                rules={[{ required: wechatpayEnabled, message: '请输入微信支付商户号' }]}
              >
                <Input placeholder="微信支付商户号（MCHID）" />
              </Form.Item>
              <Form.Item
                name="wechatpay_api_key"
                label="微信支付 API 密钥"
                rules={[{ required: wechatpayEnabled, message: '请输入微信支付 API 密钥' }]}
              >
                <Input.Password placeholder="微信支付 API 密钥（APIKEY）" />
              </Form.Item>
              <Form.Item
                name="wechatpay_cert_path"
                label="微信支付证书"
                extra="退款等高级功能需要使用证书，支持 .p12 或 .pem 格式"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input 
                    placeholder="/path/to/apiclient_cert.p12" 
                    value={wechatCertPath}
                    onChange={(e) => setWechatCertPath(e.target.value)}
                    addonAfter={
                      <Upload
                        beforeUpload={(file) => handleUploadCert('wechatpay_cert', file)}
                        showUploadList={false}
                        disabled={uploadingCert}
                      >
                        <Button size="small" icon={<UploadOutlined />} loading={uploadingCert}>
                          上传证书
                        </Button>
                      </Upload>
                    }
                  />
                  {wechatCertPath && (
                    <Text type="success">
                      <FileOutlined /> 已配置证书: {wechatCertPath}
                    </Text>
                  )}
                </Space>
              </Form.Item>
              <Form.Item
                name="wechatpay_notify_url"
                label="微信支付回调地址"
                extra="填写支付回调地址，如：https://your-domain.com/api/webhook/wechatpay"
              >
                <Input placeholder="https://your-domain.com/api/webhook/wechatpay" />
              </Form.Item>
              <Form.Item label="APIv3 密钥（32位）" extra="微信商户平台 → API安全 → APIv3密钥">
                <Input.Password
                  placeholder="32位APIv3密钥"
                  value={wechatpayApiV3Key}
                  onChange={(e) => setWechatpayApiV3Key(e.target.value)}
                />
              </Form.Item>
              <Form.Item label="商户私钥（PEM）" extra="API证书下载的 apiclient_key.pem 文件内容">
                <Input.TextArea
                  rows={4}
                  placeholder="-----BEGIN PRIVATE KEY-----..."
                  value={wechatpayPrivateKey}
                  onChange={(e) => setWechatpayPrivateKey(e.target.value)}
                />
              </Form.Item>
              <Form.Item label="证书序列号" extra="API证书 → 查看证书序列号（十六进制）">
                <Input
                  placeholder="例如：3A89F0E32B3D7B0D16097BFFE2ED53B48369C784"
                  value={wechatpayCertSerial}
                  onChange={(e) => setWechatpayCertSerial(e.target.value)}
                />
              </Form.Item>
              <Form.Item label="沙盒模式">
                <Switch
                  checkedChildren="沙盒"
                  unCheckedChildren="生产"
                  checked={wechatpaySandbox}
                  onChange={setWechatpaySandbox}
                />
                <span style={{ marginLeft: 8, color: '#6b7280' }}>
                  {wechatpaySandbox ? '当前使用微信支付沙盒环境测试' : '当前使用微信支付生产环境'}
                </span>
              </Form.Item>
            </Card>

            {/* 支付宝配置 */}
            <Card
              title={
                <span>
                  <AlipayOutlined style={{ marginRight: 8, color: '#1677ff' }} />
                  支付宝
                </span>
              }
              style={{ marginBottom: 24 }}
              extra={
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={alipayEnabled}
                  onChange={setAlipayEnabled}
                />
              }
            >
              <Alert
                message="获取支付宝 API 凭证"
                description={
                  <span>
                    请前往 <a href="https://open.alipay.com/" target="_blank" rel="noopener noreferrer">支付宝开放平台</a> 创建应用并获取配置信息。
                    Webhook 端点地址为：<code>/api/webhook/alipay</code>
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="alipay_app_id"
                label="支付宝 App ID"
                rules={[{ required: alipayEnabled, message: '请输入支付宝 App ID' }]}
              >
                <Input placeholder="支付宝应用APPID" />
              </Form.Item>
              <Form.Item
                name="alipay_private_key"
                label="应用私钥"
                rules={[{ required: alipayEnabled, message: '请输入应用私钥' }]}
              >
                <Input.TextArea 
                  placeholder="应用私钥（RSA2）" 
                  rows={4} 
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
              <Form.Item
                name="alipay_public_key"
                label="支付宝公钥"
                rules={[{ required: alipayEnabled, message: '请输入支付宝公钥' }]}
              >
                <Input.TextArea 
                  placeholder="支付宝公钥（RSA2）" 
                  rows={4} 
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
              <Form.Item
                name="alipay_return_url"
                label="支付宝同步跳转地址"
                extra="用户支付完成后跳转回的页面地址（如：https://your-site.com/recharge?success=true）"
              >
                <Input placeholder="https://your-site.com/recharge" />
              </Form.Item>
              <Form.Item
                name="alipay_notify_url"
                label="支付宝异步回调地址"
                extra="填写支付回调地址，如：https://your-domain.com/api/webhook/alipay"
              >
                <Input placeholder="https://your-domain.com/api/webhook/alipay" />
              </Form.Item>
              <Form.Item
                name="alipay_cert_path"
                label="支付宝证书"
                extra="退款等高级功能需要使用证书，支持 .pem 格式"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input 
                    placeholder="/path/to/alipay_cert.pem" 
                    value={alipayCertPath}
                    onChange={(e) => setAlipayCertPath(e.target.value)}
                    addonAfter={
                      <Upload
                        beforeUpload={(file) => handleUploadCert('alipay_cert', file)}
                        showUploadList={false}
                        disabled={uploadingCert}
                      >
                        <Button size="small" icon={<UploadOutlined />} loading={uploadingCert}>
                          上传证书
                        </Button>
                      </Upload>
                    }
                  />
                  {alipayCertPath && (
                    <Text type="success">
                      <FileOutlined /> 已配置证书: {alipayCertPath}
                    </Text>
                  )}
                </Space>
              </Form.Item>
              <Form.Item label="沙盒模式">
                <Switch
                  checkedChildren="沙盒"
                  unCheckedChildren="生产"
                  checked={alipaySandbox}
                  onChange={setAlipaySandbox}
                />
                <span style={{ marginLeft: 8, color: '#6b7280' }}>
                  {alipaySandbox ? '当前使用支付宝沙盒环境测试' : '当前使用支付宝生产环境'}
                </span>
              </Form.Item>
            </Card>

            {/* 银行卡支付配置 */}
            <Card
              title={
                <span>
                  <BankOutlined style={{ marginRight: 8, color: '#faad14' }} />
                  银行卡转账支付
                </span>
              }
              style={{ marginBottom: 24 }}
              extra={
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={offlineEnabled}
                  onChange={setOfflineEnabled}
                />
              }
            >
              <Alert
                message="银行卡转账配置"
                description={
                  <span>
                    用户选择银行卡转账后，会显示配置的银行卡信息。用户转账后需上传凭证，由管理员审核通过后手动增加余额。
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="bank_account"
                label="银行卡号"
                rules={[{ required: offlineEnabled, message: '请输入银行卡号' }]}
              >
                <Input placeholder="请输入银行卡号" />
              </Form.Item>
              <Form.Item
                name="bank_name"
                label="开户银行"
                rules={[{ required: offlineEnabled, message: '请输入开户银行名称' }]}
              >
                <Input placeholder="如：中国工商银行北京支行" />
              </Form.Item>
              <Form.Item
                name="bank_account_name"
                label="开户名称"
                extra="公司名称或个人姓名"
                rules={[{ required: offlineEnabled, message: '请输入开户名称' }]}
              >
                <Input placeholder="如：XX科技有限公司" />
              </Form.Item>
            </Card>

            {/* 保存按钮 */}
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              保存配置
            </Button>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default PaymentSettings;
